import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaChartBar, FaFilm, FaTv, FaUsers, FaHistory, FaSyncAlt, FaTimes } from 'react-icons/fa';
import './StatsPage.css';

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const PERIODS = [
  { days: 1, label: 'Últimas 24h' },
  { days: 7, label: '7 dias' },
  { days: 30, label: '30 dias' },
  { days: 90, label: '90 dias' },
  { days: 0, label: 'Tudo' }
];

const TABS = [
  { key: 'movies', label: 'Filmes', icon: <FaFilm /> },
  { key: 'series', label: 'Séries', icon: <FaTv /> },
  { key: 'users', label: 'Clientes', icon: <FaUsers /> },
  { key: 'recent', label: 'Últimos plays', icon: <FaHistory /> }
];

const fmtDate = (value) => (value ? new Date(value).toLocaleString('pt-BR') : '—');

const StatsPage = () => {
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState('movies');
  const [overview, setOverview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [lineFilter, setLineFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stats/overview`, { params: { days, limit: 200 } });
      setOverview(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar estatísticas');
    }
  }, [days]);

  const fetchRecent = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (lineFilter) params.line = lineFilter;
      const response = await axios.get(`${BACKEND_URL}/api/stats/recent`, { params });
      setRecent(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar plays recentes');
    }
  }, [lineFilter]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOverview(), fetchRecent()]);
    setLoading(false);
  }, [fetchOverview, fetchRecent]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [refresh]);

  const openLine = (line) => {
    setLineFilter(line);
    setSearch('');
    setTab('recent');
  };

  const matches = (...fields) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return fields.some((f) => String(f || '').toLowerCase().includes(q));
  };

  const totals = overview?.totals;

  const renderMovies = () => (
    <table className="stats-table">
      <thead>
        <tr><th>#</th><th>Filme</th><th>Categoria</th><th>Plays</th><th>Clientes</th><th>Último play</th></tr>
      </thead>
      <tbody>
        {(overview?.topMovies || []).filter((m) => matches(m.name, m.category)).map((m, i) => (
          <tr key={m.streamId}>
            <td>{i + 1}</td><td>{m.name}</td><td>{m.category}</td>
            <td className="stats-table__num">{m.plays}</td>
            <td className="stats-table__num">{m.users}</td>
            <td>{fmtDate(m.lastPlay)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderSeries = () => (
    <table className="stats-table">
      <thead>
        <tr><th>#</th><th>Série</th><th>Categoria</th><th>Plays</th><th>Episódios</th><th>Clientes</th><th>Último play</th></tr>
      </thead>
      <tbody>
        {(overview?.topSeries || []).filter((s) => matches(s.name, s.category)).map((s, i) => (
          <tr key={s.seriesId ?? s.name}>
            <td>{i + 1}</td><td>{s.name}</td><td>{s.category}</td>
            <td className="stats-table__num">{s.plays}</td>
            <td className="stats-table__num">{s.episodes}</td>
            <td className="stats-table__num">{s.users}</td>
            <td>{fmtDate(s.lastPlay)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderUsers = () => (
    <table className="stats-table">
      <thead>
        <tr><th>#</th><th>Cliente (linha)</th><th>Revendedor</th><th>Plays</th><th>Filmes</th><th>Episódios</th><th>Último play</th></tr>
      </thead>
      <tbody>
        {(overview?.topUsers || []).filter((u) => matches(u.line, u.owner)).map((u, i) => (
          <tr key={u.line}>
            <td>{i + 1}</td>
            <td><button className="stats-link" onClick={() => openLine(u.line)}>{u.line}</button></td>
            <td>{u.owner}</td>
            <td className="stats-table__num">{u.plays}</td>
            <td className="stats-table__num">{u.moviePlays}</td>
            <td className="stats-table__num">{u.seriesPlays}</td>
            <td>{fmtDate(u.lastPlay)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderRecent = () => (
    <>
      {lineFilter && (
        <p className="stats-filter">
          Mostrando só o cliente <strong>{lineFilter}</strong>{' '}
          <button className="stats-link" onClick={() => setLineFilter(null)}><FaTimes /> limpar</button>
        </p>
      )}
      <table className="stats-table">
        <thead>
          <tr><th>Quando</th><th>Tipo</th><th>Conteúdo</th><th>Cliente</th><th>IP</th></tr>
        </thead>
        <tbody>
          {recent.filter((r) => matches(r.name, r.line, r.ip)).map((r, i) => (
            <tr key={`${r.playedAt}-${i}`}>
              <td>{fmtDate(r.playedAt)}</td>
              <td>{r.type === 'movie' ? 'Filme' : 'Série'}</td>
              <td>{r.name}</td>
              <td><button className="stats-link" onClick={() => openLine(r.line)}>{r.line}</button></td>
              <td>{r.ip}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  return (
    <div className="movie-page">
      <h1 className="movie-page__title">Estatísticas de Visualização</h1>

      {error && <p className="movie-page__error">{error}</p>}

      <div className="movie-page__card">
        <h2 className="movie-page__card-title"><FaChartBar /> Período</h2>
        <div className="stats-periods">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              className={`movie-page__btn ${days === p.days ? 'movie-page__btn--primary' : 'stats-btn--off'}`}
              onClick={() => setDays(p.days)}
            >
              {p.label}
            </button>
          ))}
          <button className="movie-page__btn stats-btn--off" onClick={refresh} disabled={loading} title="Atualizar">
            <FaSyncAlt className={loading ? 'stats-spin' : ''} />
          </button>
        </div>

        {totals && (
          <div className="stats-tiles">
            <div className="stats-tile"><span className="stats-tile__value">{totals.plays}</span><span className="stats-tile__label">Plays</span></div>
            <div className="stats-tile"><span className="stats-tile__value">{totals.users}</span><span className="stats-tile__label">Clientes</span></div>
            <div className="stats-tile"><span className="stats-tile__value">{totals.movies}</span><span className="stats-tile__label">Filmes diferentes</span></div>
            <div className="stats-tile"><span className="stats-tile__value">{totals.episodes}</span><span className="stats-tile__label">Episódios diferentes</span></div>
          </div>
        )}
        <p className="stats-note">
          Conta cada vez que um cliente dá play num filme ou episódio pelo XUI. Repetir o mesmo conteúdo
          em menos de 30 minutos conta como um play só. Coleta ativa desde {fmtDate(totals?.firstPlay)}.
        </p>
      </div>

      <div className="movie-page__card">
        <div className="stats-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`stats-tab ${tab === t.key ? 'stats-tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
          <input
            className="stats-search"
            type="text"
            placeholder="Filtrar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="stats-table-wrap">
          {tab === 'movies' && renderMovies()}
          {tab === 'series' && renderSeries()}
          {tab === 'users' && renderUsers()}
          {tab === 'recent' && renderRecent()}
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
